import React, { useCallback, useState, useRef, useMemo, forwardRef } from 'react';
import { styled } from '@compiled/react';
import { mergeRefs } from 'use-callback-ref';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import type { TriggerProps, ContentProps } from '@atlaskit/popup';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { AsyncUpdateFilterDialog as UpdateFilterDialog } from '@atlassian/jira-save-filter-dialog/src/ui/update-filter-dialog/async.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useModalDialogActions } from '@atlassian/jira-software-modal-dialog/src/controllers/index.tsx';
import { PopupTrigger } from '@atlassian/entry-point-popup';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { ANALYTICS_PACKAGE_NAME } from '../../common/constants.tsx';
import { popupContentEntrypoint } from './popup-content/entrypoint.tsx';
import { PopupContent, preloadPopupContentComponent } from './popup-content/async.tsx';
import messages from './messages.tsx';
import { ErrorState } from './error-state/index.tsx';

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { DetailsButtonHeaderWrapper } from './styled';

export type Props = {
	filterId: string;
	defaultOpen?: boolean; // cleanup with nin_filter_details_popup_entrypoint_migration
};

const DetailsPopupOld = ({ filterId, defaultOpen = false }: Props) => {
	const { formatMessage } = useIntl();
	const { setReturnFocusTo } = useModalDialogActions();
	const triggerRef = useRef<HTMLButtonElement | null>(null);
	const [isOpen, setIsOpen] = useState(defaultOpen);
	const onClose = useCallback(() => setIsOpen(false), [setIsOpen]);
	const titleId = 'issue-navigator-custom-filters.ui.details-popup.popup.title';
	const onTriggerClick = useCallback(
		(_: unknown, analyticsEvent: UIAnalyticsEvent) => {
			setIsOpen(!isOpen);
			fireUIAnalytics(analyticsEvent, 'filterDetailsButton');
		},
		[isOpen, setIsOpen],
	);

	const [isEditFilterModalOpen, setIsEditFilterModalOpen] = useState(false);

	const onOpenEditFilterModal = useCallback(() => {
		if (!fg('nin-save-filter-dialog-entry-point')) {
			setIsEditFilterModalOpen(true);
		} else {
			setReturnFocusTo(triggerRef);
		}
		onClose();
	}, [onClose, setReturnFocusTo]);

	const onCloseEditFilterModal = useCallback(() => {
		setIsEditFilterModalOpen(false);
		setReturnFocusTo(triggerRef);
	}, [setReturnFocusTo]);

	const Content = useCallback(
		(props: ContentProps) => (
			<PopupContent
				{...props}
				triggerRef={triggerRef}
				onOpenEditFilterModal={onOpenEditFilterModal}
				onOpenNewSubscriptionModal={onClose}
				filterId={filterId}
				titleId={titleId}
			/>
		),
		[filterId, onClose, onOpenEditFilterModal],
	);
	const Trigger = useCallback(
		(props: TriggerProps) => {
			const ref = mergeRefs([props.ref, triggerRef]);
			return (
				<Button
					{...props}
					appearance="link"
					onClick={onTriggerClick}
					isSelected={isOpen}
					onMouseOver={preloadPopupContentComponent}
					testId="issue-navigator-custom-filters.ui.details-popup.button"
					ref={ref}
				>
					{formatMessage(messages.filterDetails)}
				</Button>
			);
		},
		[isOpen, onTriggerClick, formatMessage],
	);

	return (
		<>
			{!fg('nin-save-filter-dialog-entry-point') && (
				<UpdateFilterDialog
					filterId={filterId}
					onClose={onCloseEditFilterModal}
					isOpen={isEditFilterModalOpen}
				/>
			)}
			<Popup
				isOpen={isOpen}
				onClose={onClose}
				shouldRenderToParent={fg('jfp_a11y_team_fix_filter_details_reading_order')}
				placement="bottom-start"
				messageId="issue-navigator-custom-filters.ui.details-popup.popup"
				messageType="transactional"
				content={Content}
				trigger={Trigger}
				testId="issue-navigator-custom-filters.ui.details-popup.popup"
				role="dialog"
				titleId={titleId}
			/>
		</>
	);
};

const Trigger = forwardRef<
	HTMLElement,
	TriggerProps & {
		triggerRef: React.Ref<HTMLElement>;
	}
>(({ triggerRef, ...props }, ref) => {
	const { formatMessage } = useIntl();
	const mergedRef = mergeRefs([ref, triggerRef]);

	return (
		<Button
			{...props}
			appearance="link"
			isSelected={props['aria-expanded']}
			testId="issue-navigator-custom-filters.ui.details-popup.button"
			ref={mergedRef}
		>
			{formatMessage(messages.filterDetails)}
		</Button>
	);
});

const DetailsPopupNew = ({ filterId }: Props) => {
	const isAnonymous = useIsAnonymous();
	const activationId = useActivationId();
	const cloudId = useCloudId();
	const entryPointParams = useMemo(
		() => ({
			filterId,
			activationId,
			cloudId,
			isAnonymous,
		}),
		[filterId, activationId, cloudId, isAnonymous],
	);

	const triggerRef = useRef<HTMLButtonElement | null>(null);
	const titleId = 'issue-navigator-custom-filters.ui.details-popup.popup.title';

	const entryPointProps = useMemo(
		() => ({
			titleId,
			triggerRef,
		}),
		[triggerRef, titleId],
	);

	return (
		<PopupTrigger
			entryPoint={popupContentEntrypoint}
			entryPointParams={entryPointParams}
			entryPointProps={entryPointProps}
			trigger={(props) => <Trigger {...props} triggerRef={triggerRef} />}
			errorFallback={ErrorState}
			onError={(props) => {
				fireErrorAnalytics({
					...props,
					meta: {
						id: 'DetailsPopupEntryPoint',
						packageName: ANALYTICS_PACKAGE_NAME,
						teamName: 'empanada',
					},
					sendToPrivacyUnsafeSplunk: true,
				});
			}}
			shouldRenderToParent
			placement="bottom-start"
			testId="issue-navigator-custom-filters.ui.details-popup.popup"
			role="dialog"
			titleId={titleId}
			fallback={<LoadingFallback />}
		/>
	);
};

export const DetailsPopup = componentWithFG(
	'nin_filter_details_popup_entrypoint_migration',
	DetailsPopupNew,
	DetailsPopupOld,
);

const LoadingFallback = () => (
	<LoadingWrapper>
		<Spinner />
	</LoadingWrapper>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const LoadingWrapper = styled.div({
	textAlign: 'center',
	width: '440px',
	paddingTop: token('space.250'),
	paddingRight: token('space.250'),
	paddingBottom: token('space.250'),
	paddingLeft: token('space.250'),
	overflow: 'auto',
	maxHeight: 'calc(100vh - 215px)',
});
